# Privacy Policy for the Safe Learning Management System.

## 1\. Introduction

This Privacy Policy explains how Safe Asset Group International AB (“we”, “us”, or “our”) collects, uses, shares, and protects personal information obtained from users (“you” or “your”) of our Safe Learning Management System. We are committed to protecting your privacy and handling your data in an open and transparent manner.

## 2\. Information Collection

We collect information that you provide directly to us when you use our Service, or provide us with your contact information to gain access to the Service Administrative Portal.

The types of data we collect may include:

* Name  
* Email address  
* Usage data  
* Entered codes  
* Time when a course is started  
* Time when a course is completed  
* Time of last activity for a session

We only collect your name and email address if you require access to the Service Administrative Portal.

## 3\. Use of Information

We use the collected information to provide, maintain, and improve our Service.

## 4\. Data Storage and Security

At Safe Asset Group International AB, we take the security and protection of your data seriously. We implement and maintain appropriate technical and organisational security measures to protect your personal information from unauthorised access, destruction, loss, alteration, or misuse.

### 4.1 Data Storage

* We store your data on secure servers located in the European Union.  
* We regularly back up your data to prevent data loss and aid in recovery.  
* We retain your data for as long as your account is active or as needed to provide you services. We will delete your data upon request or after an extended period of inactivity, except where we are legally required to retain it.

### 4.2 Data Security Measures

* All data transmission between you and our servers is encrypted using SSL/TLS protocols.  
* We use firewalls and multi-factor authentication to prevent unauthorised access to our systems.  
* We regularly conduct security audits and vulnerability assessments to identify and address potential security risks.  
* Access to your personal data by our employees is limited and granted on a need-to-know basis. All employees with such access are bound by strict confidentiality obligations.

### 4.3 Third-Party Service Providers

* We use third-party service providers, including our server provider, to support our operations. These providers act as data processors and only process data on our instructions.  
* Our server provider, The Constant Company, LLC, stores and processes your data on our behalf. They do not have the right to use your personal data beyond what is necessary to assist us.  
* We have data processing agreements in place with all our data processors, including our server provider. These agreements require them to:  
  * Process data only on our documented instructions  
  * Implement appropriate technical and organisational measures to protect personal data  
  * Assist us in fulfilling our obligations under data protection laws  
  * Delete or return all personal data to us after the end of the provision of services  
  * Submit to audits and inspections and provide us with whatever information we need to ensure we're both meeting our Article 28 obligations (GDPR)  
* When we use third-party service providers, we disclose only the personal information that is necessary to deliver the service.  
* We regularly review the security and privacy practices of our data processors to ensure they meet our standards and legal requirements.

### 4.4 Data Breach Procedures

In the event of a data breach that affects your personal information, we will:

* Notify you via email promptly  
* Provide information about the extent of the breach  
* Inform you about the potential consequences  
* Advise you about the measures we're taking to respond to and mitigate the breach

### 4.5 Your Role in Data Security

While we implement these security measures, the transmission of information via the internet is not completely secure. We ask that you also take steps to keep your data safe:

* Keep your system and applications up to date

If you have reason to believe that your interaction with us is no longer secure (for example, if you feel that the security of your account has been compromised), please notify us immediately.

## 5\. Third-Party Sharing

We do not sell your personal information. We may share your information with Legal authorities when required by law.

## 6\. User Rights

You have the right to:

* Access your personal data  
* Correct inaccurate data  
* Request deletion of your data  
* Object to processing of your data

Please note that for users who only access courses by distributed codes, no direct personally identifiable information is collected. Neither do we collect enough other information granular enough to single out a specific individual. Thus we are unable to process Data Subject Rights Requests for these users.

## 7\. Cookies and Tracking Technologies

We do not use Tracking cookies to provide our Service. Our cookie usage is limited to essential cookies required for those accessing the Administrative Portal. Essential cookies are only set when you login to the Administrative Portal.

## 8\. Changes to the Privacy Policy

We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.

## 9\. Contact Information

If you have any questions about this Privacy Policy, please contact us at:

[info@safeassetgroup.com](mailto:info@safeassetgroup.com)  
\+46 31 201 000

Safe Asset Group International AB  
Kivra: 559072-8746  
106 31 Stockholm  
Sweden

Last updated: 2024-09-05

